/**
 * Lifecycle of a `SkeletonReveal`, independent of `isLoaded`:
 * - `drawing`: the first load's outlines are drawing in, too briefly to reveal yet.
 * - `drawingRevealable`: still drawing, but long enough that data may reveal
 *   mid-draw; the phase is `revealing` once data is there, otherwise `drawing`.
 * - `drawn`: outlines are done; the phase is `shimmering` until data arrives, then `revealing`.
 * - `settled`: no skeleton, either because data was ready on mount or the reveal finished.
 *
 * Later loads (`isLoaded` back to `false`) skip the draw and go straight to `drawn`.
 */
export type SkeletonRevealStage = "drawing" | "drawingRevealable" | "drawn" | "settled";

export type SkeletonRevealPhase = "drawing" | "shimmering" | "revealing" | "settled";

export type SkeletonRevealStageEvent =
    | "loadingStarted"
    | "drawMinimumReached"
    | "drawFinished"
    | "revealFinished";

export function getInitialStage(isLoaded: boolean): SkeletonRevealStage {
    return isLoaded ? "settled" : "drawing";
}

/** Whether the outlines are still animating in, which can overlap a mid-draw reveal. */
export function isDrawingStage(stage: SkeletonRevealStage): boolean {
    return stage === "drawing" || stage === "drawingRevealable";
}

export function getNextStage(
    stage: SkeletonRevealStage,
    event: SkeletonRevealStageEvent,
): SkeletonRevealStage {
    switch (event) {
        case "loadingStarted":
            return isDrawingStage(stage) ? stage : "drawn";
        case "drawMinimumReached":
            return stage === "drawing" ? "drawingRevealable" : stage;
        case "drawFinished":
            return isDrawingStage(stage) ? "drawn" : stage;
        case "revealFinished":
            return stage === "drawn" || stage === "drawingRevealable"
                ? "settled"
                : stage;
        default: {
            const exhaustiveEvent: never = event;
            return exhaustiveEvent;
        }
    }
}

export function getPhase(
    stage: SkeletonRevealStage,
    isLoaded: boolean,
): SkeletonRevealPhase {
    switch (stage) {
        case "drawing":
            return "drawing";
        case "drawingRevealable":
            return isLoaded ? "revealing" : "drawing";
        case "drawn":
            return isLoaded ? "revealing" : "shimmering";
        case "settled":
            return "settled";
        default: {
            const exhaustiveStage: never = stage;
            return exhaustiveStage;
        }
    }
}
